import {
  getPayoutText,
  getSelectedRolesFromEmployee,
} from '../../helpers/employees';
import {
  EmployeeToBusiness,
  LoadedUser,
  ModalData,
  SectionRow,
} from '../../interfaces';
import { convertSnakeToTitleCase, getCleanedPhone } from '../global/helper';

// Set modal data based on the employee data
export function createModalData(
  currentUser: LoadedUser,
  employee: EmployeeToBusiness,
) {
  const employeePhone = getCleanedPhone(employee.user?.phoneNumber || '');
  const bookingLink = `https://web.getporter.io/artist/${employee.urlPath}`;
  const bookAheadLimitDays = employee.bookAheadLimitDays
    ? `${JSON.stringify(employee.bookAheadLimitDays)} days`
    : '365 days';

  const payoutText = getPayoutText(employee);

  const isCommission = employee.payrollType === 'COMMISSION';

  const cleanedCommission = `${(
    employee.paymentToEmployeePercent * 100
  ).toString()}%`;

  const shopManagerPermissionsRows =
    employee.user?.role === 'SHOP_MANAGER'
      ? ([
          {
            type: 'TEXT',
            label: 'Is Bookable',
            value: [employee.isBookable ? 'Yes' : 'No'],
          },
          {
            type: 'TEXT',
            label: 'Add to all chats',
            value: [employee.addToAllChats ? 'Yes' : 'No'],
          },
          {
            type: 'TEXT',
            label: 'General Request Owner',
            value: [employee.generalRequestOwner ? 'Yes' : 'No'],
          },
          !!currentUser?.canViewReports && {
            type: 'TEXT',
            label: 'Can view reports',
            value: [employee.canViewReports ? 'Yes' : 'No'],
          },
          !!currentUser?.canManageWaivers && {
            type: 'TEXT',
            label: 'Can manage waivers',
            value: [employee.canManageWaivers ? 'Yes' : 'No'],
          },
          !!currentUser?.canManageCampaigns && {
            type: 'TEXT',
            label: 'Can manage campaigns',
            value: [employee.canManageCampaigns ? 'Yes' : 'No'],
          },
        ].filter(Boolean) as SectionRow[])
      : [];

  const allRolesPermissionsRows = [
    currentUser.canIssueRefunds
      ? {
          type: 'TEXT',
          label: 'Can issue refunds',
          value: [employee.canIssueRefunds ? 'Yes' : 'No'],
        }
      : null,
    currentUser.canViewAllShopCustomers
      ? {
          type: 'TEXT',
          label: 'Can view all customers',
          value: [employee.canViewAllShopCustomers ? 'Yes' : 'No'],
        }
      : null,
  ].filter(Boolean) as SectionRow[];

  const roles = getSelectedRolesFromEmployee(employee.user?.role!, employee);
  const roleText = roles.map(convertSnakeToTitleCase).join(', ');
  const roleRow = {
    type: 'TEXT',
    label: `Role${roles.length > 1 ? 's' : ''}`,
    value: [roleText],
  } as SectionRow;

  const permissionsRows = [
    roleRow,
    ...shopManagerPermissionsRows.concat(allRolesPermissionsRows),
  ];

  const permissionsSection = permissionsRows.length
    ? {
        name: 'Permissions',
        rows: permissionsRows,
      }
    : null;

  const newModalData = {
    name: employee.displayName || 'Artist',
    sections: [
      {
        name: 'Personal Info',
        rows: [
          {
            type: 'TEXT',
            label: 'First Name',
            value: [employee.user?.firstName || ''],
          },
          {
            type: 'TEXT',
            label: 'Last Name',
            value: [employee.user?.lastName || ''],
          },
          {
            type: 'TEXT',
            label: 'Display Name',
            value: [employee.displayName || ''],
          },
          {
            type: 'TEXT',
            label: 'Email',
            value: [employee.user?.email || ''],
          },
          {
            type: 'TEXT',
            label: 'Phone',
            value: [employeePhone],
          },
          {
            type: 'COLOR',
            label: 'Color',
            value: [employee.color || ''],
          },
        ],
      },
      {
        name: 'Payments',
        rows: [
          {
            type: 'TEXT',
            label: 'Payouts',
            value: [payoutText],
          },
          isCommission
            ? {
                type: 'TEXT',
                label: 'Commission Percent',
                value: [cleanedCommission || 'Unknown'],
              }
            : null,
        ].filter(Boolean),
      },
      {
        name: 'Booking Settings',
        rows: [
          {
            type: 'TEXT',
            label: 'Booking Link',
            value: [bookingLink],
          },
          {
            type: 'TEXT',
            label: 'Book Ahead Limit',
            value: [bookAheadLimitDays],
          },
          {
            type: 'TEXT',
            label: 'Calendar Synced',
            value: [employee.calendarEmail || 'No external calendar connected'],
          },
        ],
      },
      permissionsSection,
    ].filter(Boolean),
  };

  return newModalData as ModalData;
}
